import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  LayoutDashboard,
  MessageSquare,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Sparkles,
  X,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext.jsx";
import logo from "../../images/logo2.png";
import AppButton from "../shared/AppButton.jsx";
import { API_BASE_URL } from "../../config.js";

const navItems = [
  {
    label: "Users",
    to: "/admin",
    icon: LayoutDashboard,
  },
  {
    label: "User Queries",
    to: "/User-queries",
    icon: MessageSquare,
  },
];

const Header = ({ pageTitle, pageSubtitle, children }) => {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const onEscape = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, []);

  const handleLogout = async () => {
    const token = window.__appSession.getToken();

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        window.__appSession.clearAllSessions();
        navigate("/");
      } else {
        const errorData = await response.json();
        console.error("Logout error:", errorData.message);
      }
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <div className={`relative min-h-screen overflow-hidden ${theme.mainBg}`}>
      <div
        className="aurora-dot h-44 w-44 bg-indigo-300"
        style={{ top: "3rem", left: "-2.5rem" }}
      />
      <div
        className="aurora-dot h-60 w-60 bg-violet-300"
        style={{ top: "35%", right: "-4rem", animationDelay: "1.2s" }}
      />

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="fixed inset-0 z-40 bg-slate-900/45 md:hidden"
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0 }}
            onClick={() => setIsMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      <motion.aside
        className="fixed inset-y-0 left-0 z-30 hidden px-1.5 pb-3 pt-[82px] md:flex"
        animate={{ width: isCompact ? 96 : 248 }}
        transition={
          reduceMotion
            ? { duration: 0.01 }
            : { type: "spring", stiffness: 260, damping: 28 }
        }
      >
        <div
          className={`glass-surface ${theme.sidebarBg} ${theme.sidebarText} ${theme.sidebarBorder} flex h-full w-full flex-col rounded-2xl border p-2`}
        >
          <div className="mb-2 flex items-center justify-between rounded-xl border border-indigo-200 bg-white/75 px-2.5 py-2">
            <Link to="/admin" className="flex items-center gap-2 overflow-hidden">
              <img
                src={logo}
                alt="Logo"
                className="h-7 w-auto"
                style={{ maxWidth: isCompact ? "28px" : "130px" }}
              />
            </Link>
            <button
              onClick={() => setIsCompact((prev) => !prev)}
              className="rounded-lg border border-indigo-200 bg-white/80 p-1.5 text-slate-700 transition hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
              aria-label="Toggle compact sidebar"
              aria-expanded={!isCompact}
            >
              {isCompact ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </button>
          </div>

          <nav className="flex-1 space-y-1">
            {navItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to;

              return (
                <motion.div
                  key={item.to}
                  initial={reduceMotion ? false : { opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.04 }}
                >
                  <Link
                    to={item.to}
                    title={item.label}
                    aria-current={isActive ? "page" : undefined}
                    className={`group flex items-center rounded-xl border px-2.5 py-2 text-sm font-semibold transition-all ${
                      isActive
                        ? "border-indigo-300/90 bg-gradient-to-r from-indigo-100 to-violet-100 text-indigo-800 shadow-[0_12px_22px_-18px_rgba(79,70,229,0.42)]"
                        : `border-transparent ${theme.sidebarText} ${theme.sidebarHover} hover:border-indigo-200`
                    }`}
                  >
                    <span
                      className={`mr-2 inline-flex h-8 w-8 items-center justify-center rounded-lg ${
                        isActive
                          ? "border border-indigo-200 bg-white text-indigo-700"
                          : "bg-white text-indigo-600 border border-indigo-100"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span
                      className={`whitespace-nowrap transition-opacity ${
                        isCompact ? "opacity-0 pointer-events-none w-0" : "opacity-100"
                      }`}
                    >
                      {item.label}
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </nav>

          <div className="mt-2 space-y-2">
            <div className="rounded-xl border border-indigo-200 bg-white/75 px-2.5 py-2 text-[11px] text-slate-600">
              <p className="font-semibold text-slate-800">Admin Workspace</p>
              <p className="mt-0.5">Control users, subscriptions, and support flow.</p>
            </div>
            <AppButton
              variant="danger"
              size="sm"
              className={`w-full ${isCompact ? "px-0" : ""}`}
              onClick={handleLogout}
              title="Logout"
            >
              <LogOut className={`h-4 w-4 ${isCompact ? "" : "mr-2"}`} />
              {!isCompact && "Logout"}
            </AppButton>
          </div>
        </div>
      </motion.aside>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.aside
            className="fixed inset-y-0 left-0 z-50 w-72 p-3 md:hidden"
            initial={reduceMotion ? false : { x: -260 }}
            animate={{ x: 0 }}
            exit={reduceMotion ? { x: 0 } : { x: -260 }}
            transition={
              reduceMotion
                ? { duration: 0.01 }
                : { type: "spring", stiffness: 300, damping: 30 }
            }
          >
            <div
              className={`glass-surface-strong ${theme.sidebarBg} ${theme.sidebarText} ${theme.sidebarBorder} flex h-full flex-col rounded-2xl border p-3`}
            >
              <div className="mb-4 flex items-center justify-between">
                <img src={logo} alt="Logo" className="h-8 w-auto max-w-[140px]" />
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="rounded-lg border border-indigo-200 p-1.5 text-slate-600 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                  aria-label="Close menu"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <nav className="space-y-1">
                {navItems.map((item, index) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.to;
                  return (
                    <motion.div
                      key={item.to}
                      initial={reduceMotion ? false : { opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.04 }}
                    >
                      <Link
                        to={item.to}
                        onClick={() => setIsMenuOpen(false)}
                        aria-current={isActive ? "page" : undefined}
                        className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-all ${
                          isActive
                            ? "border-indigo-300/90 bg-gradient-to-r from-indigo-100 to-violet-100 text-indigo-800"
                            : `border-transparent ${theme.sidebarText} ${theme.sidebarHover} hover:border-indigo-200`
                        }`}
                      >
                        <span
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${
                            isActive
                              ? "border border-indigo-200 bg-white text-indigo-700"
                              : "bg-white text-indigo-600 border border-indigo-100"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <span>{item.label}</span>
                      </Link>
                    </motion.div>
                  );
                })}
              </nav>

              <AppButton
                variant="danger"
                size="sm"
                className="mt-auto w-full"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </AppButton>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      <div
        className={`relative ${isCompact ? "md:ml-[96px]" : "md:ml-[248px]"} px-1.5 pt-[76px] pb-2 transition-all duration-300 sm:px-3 sm:pt-[84px] sm:pb-3`}
      >
        <header className="mb-1">
          <motion.div
            className={`glass-surface-strong ${theme.headerBg} ${theme.headerBorder} ${theme.headerText} flex items-center justify-between gap-3 rounded-2xl border px-3 py-2 sm:px-3.5`}
            initial={reduceMotion ? false : { y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div>
              <div className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                <Sparkles className="h-3 w-3" />
                Control Center
              </div>
              <h1 className="mt-1 text-base font-semibold text-slate-800 md:text-lg">
                {pageTitle || "Admin Panel"}
              </h1>
              {pageSubtitle && (
                <p className="mt-0.5 text-xs text-slate-600">{pageSubtitle}</p>
              )}
            </div>
            <button
              onClick={() => setIsMenuOpen(true)}
              className="rounded-xl border border-indigo-200 bg-white/80 p-2 text-slate-700 transition hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 md:hidden"
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
            >
              <Menu className="h-5 w-5" />
            </button>
          </motion.div>
        </header>

        <main className="glass-surface rounded-2xl border border-indigo-200/60 p-1 sm:p-2">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Header;
